import {
  canonicalEmoji,
  EMOJI_SEQUENCE_SOURCE,
  isSingleEmoji,
  MESSAGE_LIMITS,
} from '@twinden/shared';

// Emojis (M69, docs/emojis.md): the rules that need no dataset. The data
// itself (names and keywords) is lazy-loaded by lib/emojiData.ts.

/** How many emojis "Recently used" keeps, newest first (D263). */
export const RECENT_EMOJI_CAP = 24;
/** Slots in the quick reaction row (D21, D264). */
export const QUICK_REACTION_SLOTS = 6;

/** The system's colour emoji fonts, on the device; never a web font. */
export const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Segoe UI Symbol", sans-serif';

// One spelling per emoji (👍 = 👍️), shared with the server (D264).
export { canonicalEmoji };

/** Newest first, no repeats, at most `cap` (D263, D264). */
export function pushRecent(
  list: readonly string[],
  emoji: string,
  cap = RECENT_EMOJI_CAP
): string[] {
  const value = canonicalEmoji(emoji);
  return [value, ...list.filter((entry) => entry !== value)].slice(0, cap);
}

/**
 * The quick reaction row (D264): your most recent reactions first, then the
 * D21 defaults you haven't used, six in all.
 */
export function quickReactions(recent: readonly string[]): string[] {
  const row: string[] = [];
  for (const emoji of [...recent, ...MESSAGE_LIMITS.QUICK_REACTIONS]) {
    const value = canonicalEmoji(emoji);
    if (!row.includes(value)) row.push(value);
    if (row.length === QUICK_REACTION_SLOTS) break;
  }
  return row;
}

/** Caret-aware insert (D263): replaces the selection, caret after it. */
export function insertAtCaret(
  text: string,
  start: number,
  end: number,
  insert: string
): { text: string; caret: number } {
  const from = Math.max(0, Math.min(start, text.length));
  const to = Math.max(from, Math.min(end, text.length));
  return {
    text: text.slice(0, from) + insert + text.slice(to),
    caret: from + insert.length,
  };
}

// ---------------------------------------------------------------------------
// Skin tones (D263): the five Fitzpatrick modifiers, or none.

export const SKIN_TONES = [
  'none',
  'light',
  'medium-light',
  'medium',
  'medium-dark',
  'dark',
] as const;
export type SkinTone = (typeof SKIN_TONES)[number];

/** The modifier code point for a tone (none has none). */
export const SKIN_MODIFIER: Record<Exclude<SkinTone, 'none'>, number> = {
  light: 0x1f3fb,
  'medium-light': 0x1f3fc,
  medium: 0x1f3fd,
  'medium-dark': 0x1f3fe,
  dark: 0x1f3ff,
};

/** The hand that shows each tone on the selector. */
export function toneSample(tone: SkinTone): string {
  return tone === 'none'
    ? '✋'
    : `✋${String.fromCodePoint(SKIN_MODIFIER[tone])}`;
}

/**
 * Which single tone a skin variant has, from its hexcode: every modifier in
 * it must be the same tone (a couple with two different tones has none, and
 * is left out of the selector's choice).
 */
export function toneOfHexcode(hexcode: string): SkinTone | null {
  const modifiers = hexcode
    .split('-')
    .map((part) => parseInt(part, 16))
    .filter((cp) => cp >= 0x1f3fb && cp <= 0x1f3ff);
  if (modifiers.length === 0) return null;
  const first = modifiers[0]!;
  if (modifiers.some((cp) => cp !== first)) return null;
  const tone = (
    Object.entries(SKIN_MODIFIER) as Array<[SkinTone, number]>
  ).find(([, cp]) => cp === first);
  return tone ? tone[0] : null;
}

/** The emoji in the chosen tone, when it has one (D263). */
export function withSkinTone(
  entry: { emoji: string; skins?: Partial<Record<SkinTone, string>> },
  tone: SkinTone
): string {
  if (tone === 'none') return entry.emoji;
  return entry.skins?.[tone] ?? entry.emoji;
}

// ---------------------------------------------------------------------------
// Big emoji messages (D265).

const EMOJI_RUN = new RegExp(EMOJI_SEQUENCE_SOURCE, 'gu');

type SegmenterCtor = new () => {
  segment(input: string): Iterable<{ segment: string }>;
};

function segmenter(): SegmenterCtor | undefined {
  return (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter;
}

/** The emojis in `text` if it is nothing but emojis and spaces, else null. */
export function emojiOnly(text: string): string[] | null {
  const Segmenter = segmenter();
  let parts: string[];
  if (Segmenter) {
    parts = [...new Segmenter().segment(text)]
      .map((part) => part.segment)
      .filter((part) => !/^\s+$/u.test(part));
  } else {
    // No Intl.Segmenter (older browsers): find emoji runs with the regex and
    // require nothing else but whitespace in between.
    parts = text.match(EMOJI_RUN) ?? [];
    if (text.replace(EMOJI_RUN, '').trim() !== '') return null;
  }
  if (parts.length === 0) return null;
  return parts.every(isSingleEmoji) ? parts : null;
}

/** Font size for an emoji-only message of 1–3 emojis, else null (D265). */
export function bigEmojiSize(text: string): number | null {
  // Three of the longest sequences (a tag flag is 14 code units) plus spaces:
  // anything longer is text, without segmenting it on every render.
  if (text.length > 64) return null;
  const emojis = emojiOnly(text);
  if (!emojis || emojis.length > 3) return null;
  return [40, 34, 28][emojis.length - 1]!;
}
